import {
  Anchor,
  Button,
  Checkbox,
  ConfirmDialog,
  Div,
  Flex,
  HStack,
  OrderCard,
  Spacer,
  Text,
} from '@sendoutcards/quantum-design-ui'
import React from 'react'
import { usePersistedUserData, useSelector, useState } from 'src/hooks'
import { AffiliateFormInfo } from 'src/marketing/plans/components/AffiliateForm/AffiliateForm'
import { EditCard, PaymentTab } from 'src/payments/components'
import { creditCardBrand } from 'src/payments/components/CardLogo/CreditCardLogo'
import { FlexRow } from 'src/styled'

interface PaymentMethodProps {
  affiliateInfo: AffiliateFormInfo
  onFinishAccount: (info: AffiliateFormInfo) => void
  onBack: (info: AffiliateFormInfo) => void
  shouldHideAffiliateFields?: true
}

interface PaymentMethodState {
  accepted: boolean
  stripeToken?: stripe.Token
}

const PaymentMethod: React.FC<PaymentMethodProps> = props => {
  const {
    affiliateInfo,
    onFinishAccount,
    onBack,
    shouldHideAffiliateFields,
  } = props

  const { account } = usePersistedUserData()

  const [details, setDetails] = useState<PaymentMethodState>({
    accepted: affiliateInfo?.accepted ?? false,
    stripeToken: affiliateInfo?.stripeToken ?? undefined,
  })
  const [errorMessage, setErrorMessage] = useState<string>()

  const card =
    details.stripeToken?.card ??
    (account?.stripeSource?.__typename === 'CreditCard'
      ? { last4: account.stripeSource.last4, brand: account.stripeSource.brand }
      : undefined)

  const onBack2 = () => {
    onBack({
      ...affiliateInfo,
      accepted: details.accepted,
      stripeToken: details.stripeToken,
    })
  }

  const onSubmit = () => {
    if (isDisabled) {
      return
    }
    onFinishAccount({
      ...affiliateInfo,
      accepted: details.accepted,
      stripeToken: details.stripeToken,
    })
    return
  }

  const handleChange = (
    name: keyof PaymentMethodState,
    value: string | boolean,
  ) => {
    setDetails({ ...details, [name]: value })
  }

  const submitCard = (token: stripe.Token) => {
    setDetails({ ...details, stripeToken: token })
  }

  const removeCard = () => {
    setDetails({ ...details, stripeToken: undefined })
  }

  const onDialogClose = () => {
    setErrorMessage(undefined)
  }

  const isMobile = useSelector(state => state.window.isMobile)

  const isDisabled = !details.accepted || !card

  return (
    <form>
      {!shouldHideAffiliateFields ? (
        <>
          <Flex width="100%" flexDirection="column">
            <Spacer space="x2" />
            <OrderCard
              price="$14/month"
              thumbnail={{
                image:
                  'https://images.unsplash.com/photo-1521685468847-de0a1a3c41a8?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=3300&q=80',
                orientation: 'portrait',
              }}
              title="Monthly Affiliate Fee"
            />
            <Spacer space={'x4'} />
            {card ? (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                }}
              >
                <PaymentTab
                  paymentType={'card'}
                  last4={card.last4}
                  brand={creditCardBrand(card.brand)}
                />
                <Button
                  fullWidth={false}
                  type="secondary"
                  title="Remove Card"
                  onClick={() => removeCard()}
                />
              </div>
            ) : (
              <EditCard
                onSuccess={submitCard}
                onError={setErrorMessage}
                isSubmitButtonVisible={true}
                isAddingCard={false}
              />
            )}
            <Spacer space={'x4'} />
            <FlexRow>
              <Div outset={{ top: 'x1' }}>
                <Checkbox
                  onChange={accepted => handleChange('accepted', accepted)}
                  isChecked={details.accepted}
                  type="primary"
                />
              </Div>
              <Text type="caption">
                I agree to the Affiliate Agreement, Compensation Plan, and Terms
                and Conditions. I also authorize Greener Still to charge me
                $14/month to maintain my Affiliate websites, apps, etc.
                necessary to facilitate my business.
              </Text>
            </FlexRow>
            <Spacer space={'x2'} />
            <Div inset={{ left: 'x6', right: 'x4' }}>
              <HStack justify="space-between" gap="x9">
                <Anchor
                  href="https://www.sendoutcards.com/resources/documents/391/Affiliate_Agreement_Documents_July_1_2020.pdf"
                  title="Agreement"
                  color="anchorBlue"
                  size="xSmall"
                />
                <Anchor
                  href="https://www.sendoutcards.com/resources/documents/275/COMPENSATION_PLAN_DETAILS_June_2020.pdf"
                  title="Comp Plan Details"
                  color="anchorBlue"
                  size="xSmall"
                />
              </HStack>
            </Div>
            <Spacer space={'x4'} />
          </Flex>
        </>
      ) : (
        <Flex width="100%" flexDirection="column">
          <Text type="subtitle">Enter Card Information</Text>
          <Spacer space="x2" />
          {card ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <PaymentTab
                paymentType={'card'}
                last4={card.last4}
                brand={creditCardBrand(card.brand)}
              />
              <Button
                fullWidth={false}
                type="secondary"
                title="Remove Card"
                onClick={() => removeCard()}
              />
            </div>
          ) : (
            <EditCard
              onSuccess={submitCard}
              onError={setErrorMessage}
              isSubmitButtonVisible={true}
              isAddingCard={false}
            />
          )}
        </Flex>
      )}
      <Spacer space={'x2'} />
      <ConfirmDialog
        title={'Card Error'}
        description={
          errorMessage ?? 'There was an error with your card. Please try again.'
        }
        isOpen={!!errorMessage}
        accept={{ title: 'Ok', onClick: onDialogClose }}
      />
      <Flex width="100%" flexDirection="column">
        {!shouldHideAffiliateFields && (
          <>
            <Spacer space={isMobile ? 'x1' : 'x4'} />
            <Div width="100%" id="finish_affiliate_account_btn">
              <Button
                fullWidth={true}
                type={'primary'}
                onClick={() => onSubmit()}
                title="Finish Joining"
                disabled={isDisabled}
              />
            </Div>
          </>
        )}
        <Spacer space={isMobile ? 'x1' : 'x2'} />
        <Div width="100%" id="back_btn">
          <Button
            fullWidth={true}
            type={'accent'}
            outlined={true}
            onClick={() => onBack2()}
            title={
              !shouldHideAffiliateFields
                ? 'Back to Identity Information'
                : 'Back to Create Account Form'
            }
          />
        </Div>
      </Flex>
      <Spacer space="x2" />
    </form>
  )
}

export default PaymentMethod
